import { useState, useCallback, useEffect } from 'react';
import Layout from '../components/Layout';
import { apiService } from '../services/apiService';
import TalentTable from '../components/TalentTable';
import AuditLogTable from '../components/AuditlogTable';
import TalentMap from '../components/TalentMap';
import CreateOrEditTalentModal from '../components/CreateOrEditTalentModal';
import AdminTalentDetailModal from '../components/AdminTalentDetailModal';
import TalentTransferModal from '../components/TalentTransferModal';
import UserManagementModal from '../components/UserManagementModal';
import DuplicatePhotosModal from '../components/DuplicatePhotosModal';
import SystemNotificationManagerModal from '../components/SystemNotificationManagerModal';
import CollapsibleFilterPanel from '../components/CollapsibleFilterPanel';
import AdminAnalytics from '../components/AdminAnalytics';
import ClientManagementPanel from '../components/ClientManagementPanel';
import SystemNotificationPanel from '../components/SystemNotificationPanel';
import JobSeekerRegistrationsPanel, { JobSeekerActionCard } from '../components/JobSeekerRegistrationsPanel';
import SpecifiedSkillNewsPanel, { NewsActionCard } from '../components/SpecifiedSkillNewsPanel';
import { useAuth } from '../context/AuthContext';
import { useTalentSearch } from '../hooks/useTalentSearch';
import { useTranslation } from 'react-i18next';
import { getInitialFilters, getFilterConfigByRole, filtersToApiParams } from '../config/filterConfig';
import { getActiveLoanEntries, isRentalLoanEntry } from '../constants/loanCategories';
import '../styles/CollapsibleLayout.css';

const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};

export default function AdminDashboard() {
    const { user } = useAuth();
    const [view, setView] = useState('home');
    const { t } = useTranslation();

    // 侧边栏折叠状态
    const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
    const [filterConfig, setFilterConfig] = useState([]);

    // 编辑模态框的状态
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingTalent, setEditingTalent] = useState(null);

    // 查看详情模态框的状态
    const [viewingTalentId, setViewingTalentId] = useState(null);

    // 人才转让模态框的状态
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [transferringTalent, setTransferringTalent] = useState(null);

    // 用户管理模态框的状态
    const [isUserManagementModalOpen, setIsUserManagementModalOpen] = useState(false);

    // 重复照片检测模态框的状态
    const [isDuplicatePhotosModalOpen, setIsDuplicatePhotosModalOpen] = useState(false);
    const [isSystemNotificationModalOpen, setIsSystemNotificationModalOpen] = useState(false);

    const {
        talents,
        isLoading: isTalentLoading,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        fetchTalents,
        currentPage,
        setCurrentPage,
        totalPages,
        totalCount,
        feeTotals,
        filters,
        setFilters,
    } = useTalentSearch();

    // 加载筛选配置（包括动态选项）
    useEffect(() => {
        const loadConfig = async () => {
            let config = getFilterConfigByRole(user.role);

            // 加载动态字段（如销售员列表）
            const dynamicFields = config.filter(f => f.dynamic);
            if (dynamicFields.length > 0) {
                for (const field of dynamicFields) {
                    if (field.id === 'salesStaffId') {
                        try {
                            const salesUsers = await apiService.getSalesUsers();
                            field.options = [
                                { value: '', label: 'すべて' },
                                ...salesUsers.map(u => ({
                                    value: u.id,
                                    label: u.username,
                                })),
                            ];
                        } catch (error) {
                            console.error('Failed to load sales users:', error);
                            field.options = [{ value: '', label: 'すべて' }];
                        }
                    }
                }
            }

            setFilterConfig(config);
            setFilters(getInitialFilters(config));
        };

        loadConfig();
    }, [user.role, setFilters]);

    const [otherData, setOtherData] = useState([]);
    const [isOtherLoading, setIsOtherLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const buildInitialCostTalentFilters = useCallback((payload = {}) => {
        const baseFilters = filterConfig.length > 0 ? getInitialFilters(filterConfig) : {};
        const nextFilters = {
            ...baseFilters,
            employmentStatus: '在職',
            initialCost: { min: '1', max: '' },
            initialCostNotCharged: null,
            initialCostUnregistered: null
        };

        const selectedSalesStaffId = payload.selectedSalesStaffId;
        if (selectedSalesStaffId && selectedSalesStaffId !== 'all') {
            if (selectedSalesStaffId === 'unassigned') {
                nextFilters.salesStaffId = '';
                nextFilters.salesStaffUnassigned = true;
            } else {
                nextFilters.salesStaffId = selectedSalesStaffId;
                nextFilters.salesStaffUnassigned = null;
            }
        }

        if (payload.startMonth) {
            nextFilters.initialCostStartMonth = payload.startMonth;
        }

        if (payload.endMonth) {
            nextFilters.initialCostEndMonth = payload.endMonth;
        }

        return nextFilters;
    }, [filterConfig]);

    const handleViewInitialCostTalents = useCallback((payload = {}) => {
        setView('talents');
        setSearchTerm('');
        setSortBy('createdAt');
        setSortDirection('desc');
        setCurrentPage(1);
        setFilters(buildInitialCostTalentFilters(payload));
    }, [buildInitialCostTalentFilters, setCurrentPage, setFilters, setSearchTerm, setSortBy, setSortDirection]);

    const handleViewAllTalents = useCallback(() => {
        setView('talents');
        fetchTalents();
    }, [fetchTalents]);

    const handleViewClients = useCallback(() => {
        setView('clients');
    }, []);

    const fetchAuditLogs = useCallback(async () => {
        setIsOtherLoading(true); setView('logs');
        try { setOtherData(await apiService.getAuditLogs()); }
        catch (error) { console.error(error); alert(t('fetchAuditLogsFailed')); }
        finally { setIsOtherLoading(false); }
    }, [t]);

    const handleExportCSV = useCallback(async () => {
        setIsExporting(true);
        try {
            // 转换筛选为API参数
            const filterParams = filtersToApiParams(filters);

            // Fetch all talents with current search/sort/filter parameters
            const allData = await apiService.getTalents({
                searchTerm: searchTerm,
                sortBy: sortBy,
                descending: sortDirection === 'desc',
                noPagination: true,
                ...filterParams,
            });

            const talents = allData.data || allData;

            // Fetch complete details for each talent (including residence and loan info)
            const completeData = await Promise.all(
                talents.map(async (talent) => {
                    try {
                        const [talentDetails, residenceInfo, loanInfo] = await Promise.all([
                            apiService.getTalentById(talent.id),
                            apiService.getResidenceByTalentId(talent.id).catch(() => null),
                            apiService.getLoanByTalentId(talent.id).catch(() => null)
                        ]);
                        return { ...talent, ...talentDetails, residence: residenceInfo, loan: loanInfo };
                    } catch (error) {
                        console.error(`Error fetching details for talent ${talent.id}:`, error);
                        return talent;
                    }
                })
            );

            // Map to CSV format with ALL fields
            const csvData = completeData.map((talent, index) => {
                const residence = talent.residence || talent.residencePermit;
                const loan = talent.loan;
                const loanEntries = getActiveLoanEntries(loan);
                const computedRentalLoanAmount = loanEntries
                    .filter(isRentalLoanEntry)
                    .reduce((sum, entry) => sum + toNumber(entry.amount ?? entry.Amount), 0);
                const computedAdvanceLoanAmount = loanEntries
                    .filter(entry => !isRentalLoanEntry(entry))
                    .reduce((sum, entry) => sum + toNumber(entry.amount ?? entry.Amount), 0);
                const rentalLoanAmount = toNumber(
                    talent.activeLoanRentalAmount
                    ?? talent.ActiveLoanRentalAmount
                    ?? loan?.rentalLoanAmount
                    ?? loan?.RentalLoanAmount
                    ?? computedRentalLoanAmount
                );
                const advanceLoanAmount = toNumber(
                    talent.activeLoanAdvanceAmount
                    ?? talent.ActiveLoanAdvanceAmount
                    ?? loan?.advanceLoanAmount
                    ?? loan?.AdvanceLoanAmount
                    ?? computedAdvanceLoanAmount
                );

                return {
                    // 基本情報
                    'No.': index + 1,
                    '氏名': talent.fullName || '',
                    '氏名（フリガナ）': talent.fullNameFurigana || '',
                    '管理番号': talent.managementNumber || '',
                    '性別': talent.gender || '',
                    '生年月日': talent.dateOfBirth || '',
                    '国籍': talent.nationality || '',
                    '分野': talent.field || '',
                    '住所': talent.address || '',
                    '電話番号': talent.phoneNumber || '',
                    '進歩状況': talent.employmentStatus || '',
                    '責任担当': talent.salesStaff?.username || '',

                    // 所属機関情報
                    '所属機関名': talent.affiliatedOrganizationName || '',
                    '所属機関電話': talent.affiliatedOrganizationPhone || '',
                    '所属機関メール': talent.affiliatedOrganizationEmail || '',
                    '所属機関担当者': talent.affiliatedOrganizationContact || '',

                    // 事業所情報
                    '事業所名': talent.workplaceName || '',
                    '事業所住所': talent.workplaceAddress || '',
                    '事業所電話': talent.workplacePhone || '',
                    '事業所メール': talent.workplaceEmail || '',
                    '事業所担当者': talent.workplaceContact || '',
                    '事業所担当者役職': talent.workplaceContactTitle || '',

                    // 費用情報
                    '初期費用': talent.initialCost || 0,
                    '紹介費': talent.introductionFee ?? '',
                    '管理費': talent.managementFee || 0,
                    '首月管理費': talent.firstMonthManagementFee ?? '',
                    '料金プラン': talent.billingPlanType || '',
                    '管理費含む月数': talent.includedManagementMonths ?? '',
                    '第1段階(月数)': talent.tier1Months ?? '',
                    '第1段階管理費': talent.tier1ManagementFee ?? '',
                    '第2段階(月数)': talent.tier2Months ?? '',
                    '第2段階管理費': talent.tier2ManagementFee ?? '',
                    '契約終了日': talent.contractEndDate ?? '',
                    '休止開始日': talent.employmentPauseStartDate ?? '',
                    '休止終了日': talent.employmentPauseEndDate ?? '',

                    // 在留情報
                    '在留カード番号': residence?.residenceCardNumber || '',
                    '在留カード有効期限': residence?.residenceCardExpiry || '',
                    'パスポート番号': residence?.passportNumber || '',
                    'パスポート有効期限': residence?.passportExpiry || '',
                    '申請日': residence?.applicationDate || '',
                    '交付日': residence?.grantDate || '',
                    '申請方法': residence?.applicationMethod || '',
                    '申請番号': residence?.applicationNumber || '',
                    '在留資格': residence?.residenceStatus || '',
                    '申請': residence?.applicationType || '',
                    '現在の在留資格': talent.currentResidenceStatus || '',
                    '申請者': residence?.applicant || '',
                    '面接日': residence?.interviewDate || '',
                    '入職日': residence?.startDate || '',
                    '退職日': residence?.endDate || '',
                    '更新日': residence?.renewalDate || '',
                    '更新交付日': residence?.renewalGrantDate || '',
                    '在留ステータス': residence?.status || '',
                    '残り日数': residence?.remainingDays ?? '',

                    // 貸付情報
                    '賃貸貸付額': rentalLoanAmount,
                    '立替金': advanceLoanAmount,
                    '貸付総額': loan?.totalAmount || 0,
                    '貸付詳細': loan?.details || '',
                    '貸付日': loan?.loanDate || '',
                    '返済開始日': loan?.repaymentStartDate || '',
                    '返済終了日': loan?.repaymentEndDate || '',
                    '残高': loan?.remainingBalance ?? '',
                    '返済回数': loan?.repayments?.length || 0,
                };
            });

            // Convert to CSV format
            const headers = Object.keys(csvData[0]);
            const csvContent = [
                headers.join(','),
                ...csvData.map(row =>
                    headers.map(header => {
                        const value = String(row[header] || '');
                        // Escape values containing commas, quotes, or newlines
                        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                            return `"${value.replace(/"/g, '""')}"`;
                        }
                        return value;
                    }).join(',')
                )
            ].join('\n');

            // Add BOM for Excel compatibility
            const bom = '\uFEFF';
            const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            const timestamp = new Date().toISOString().slice(0, 10);
            link.download = `人材リスト_完全版_${timestamp}.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            alert(t('exportSuccess'));
        } catch (error) {
            console.error('CSV export failed:', error);
            alert(`${t('exportFailed')}: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    }, [searchTerm, sortBy, sortDirection, filters, t]);

    const handleEdit = (talent) => { setEditingTalent(talent); setIsEditModalOpen(true); };
    const handleAddNew = () => { setEditingTalent(null); setIsEditModalOpen(true); };
    const handleCloseEditModal = () => { setIsEditModalOpen(false); setEditingTalent(null); };

    const handleViewDetails = (talentId) => {
        setViewingTalentId(talentId);
    };
    const handleViewEdit = (talentId) => {
        setViewingTalentId(talentId);
    };
    const handleCloseViewModal = () => {
        setViewingTalentId(null);
    };

    const handleTransferTalent = (talent) => {
        setTransferringTalent(talent);
        setIsTransferModalOpen(true);
    };
    const handleCloseTransferModal = () => {
        setIsTransferModalOpen(false);
        setTransferringTalent(null);
    };
    const handleTransferSuccess = () => {
        fetchTalents(); // 刷新人才列表
    };


    // 只在非首页时显示顶部操作按钮
    const actions = view !== 'home' ? (
        (view === 'clients' || view === 'jobseekers') ? (
            <div className="flex gap-2 w-full sm:w-auto">
                <button onClick={() => setView('home')} className="btn-base bg-purple-600 text-white hover:bg-purple-700 shadow-md">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                    </svg>
                    {t('backToHome')}
                </button>
            </div>
        ) : (
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
                <button onClick={() => setView('home')} className="btn-base bg-purple-600 text-white hover:bg-purple-700 shadow-md">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                    </svg>
                    {t('backToHome')}
                </button>
                <button onClick={handleViewAllTalents} className="btn-base btn-primary shadow-md">{t('viewAllTalents')}</button>
                <button onClick={handleAddNew} className="btn-base bg-green-600 text-white hover:bg-green-700 shadow-md">{t('newTalent')}</button>
                <button onClick={() => setView('map')} className="btn-base bg-green-600 text-white hover:bg-green-700 shadow-md">{t('talentMap')}</button>
                {user.role === 'SuperAdmin' && (
                    <>
                        <button onClick={() => setIsUserManagementModalOpen(true)} className="btn-base bg-purple-600 text-white hover:bg-purple-700 shadow-md">{t('manageUsers')}</button>
                        <button onClick={() => setIsSystemNotificationModalOpen(true)} className="btn-base bg-indigo-600 text-white hover:bg-indigo-700 shadow-md">
                            {t('systemNotifications.openManager')}
                        </button>
                    </>
                )}
                <button onClick={fetchAuditLogs} className="btn-base btn-secondary shadow-md">{t('viewAuditLogs')}</button>
                {(user.role === 'Admin' || user.role === 'SuperAdmin') && (
                    <button onClick={() => setIsDuplicatePhotosModalOpen(true)} className="btn-base bg-orange-600 text-white hover:bg-orange-700 shadow-md">
                        🔍 重複照片検測
                    </button>
                )}
            </div>
        )
    ) : null;

    return (
        <>
            <CreateOrEditTalentModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} onFinished={handleViewAllTalents} existingTalent={editingTalent} />
            <AdminTalentDetailModal isOpen={!!viewingTalentId} onClose={handleCloseViewModal} talentId={viewingTalentId} />
            <TalentTransferModal isOpen={isTransferModalOpen} onClose={handleCloseTransferModal} talent={transferringTalent} onTransferSuccess={handleTransferSuccess} />
            <UserManagementModal isOpen={isUserManagementModalOpen} onClose={() => setIsUserManagementModalOpen(false)} />
            <DuplicatePhotosModal isOpen={isDuplicatePhotosModalOpen} onClose={() => setIsDuplicatePhotosModalOpen(false)} />
            <SystemNotificationManagerModal isOpen={isSystemNotificationModalOpen} onClose={() => setIsSystemNotificationModalOpen(false)} />

            <Layout title={t('adminDashboard')} actions={actions} fullWidth={view === 'jobseekers' || view === 'talents' || view === 'clients' || view === 'news'}>
                {view === 'home' && (
                    <div className="space-y-8">
                        {/* 区域标题：快速操作 */}
                        <div className="section-header">
                            <div className="section-header-icon">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="section-header-title">クイックアクション</h2>
                                <p className="section-header-subtitle">よく使う機能にすばやくアクセス</p>
                            </div>
                        </div>

                        {/* Bento Grid 快速操作卡片 */}
                        <div className="dashboard-bento-grid">
                            {/* 新規人材登録 */}
                            <button
                                onClick={handleAddNew}
                                className="action-card action-card--green"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('newTalent')}</span>
                                <span className="action-card-subtitle">新しい人材を登録</span>
                            </button>

                            {/* 人才列表 */}
                            <button
                                onClick={handleViewAllTalents}
                                className="action-card action-card--blue"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('viewAllTalents')}</span>
                                <span className="action-card-subtitle">人材リストを表示</span>
                            </button>

                            {/* 人才地图 */}
                            <button
                                onClick={() => setView('map')}
                                className="action-card action-card--green"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('talentMap')}</span>
                                <span className="action-card-subtitle">地図で確認</span>
                            </button>

                            <button
                                onClick={handleViewClients}
                                className="action-card action-card--indigo"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M5 7l1.5-2h11L19 7M5 7v11a2 2 0 002 2h10a2 2 0 002-2V7M9 11h6M9 15h4" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('clientManagement.quickAction')}</span>
                                <span className="action-card-subtitle">機関と事業所を管理</span>
                            </button>

                            {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
                                <JobSeekerActionCard onClick={() => setView('jobseekers')} />
                            )}

                            <NewsActionCard onClick={() => setView('news')} />

                            {/* 用户管理 - SuperAdmin 专用 */}
                            {user.role === 'SuperAdmin' && (
                                <button
                                    onClick={() => setIsUserManagementModalOpen(true)}
                                    className="action-card action-card--purple"
                                >
                                    <div className="action-card-icon">
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    </div>
                                    <span className="action-card-title">{t('manageUsers')}</span>
                                    <span className="action-card-subtitle">ユーザー設定</span>
                                </button>
                            )}

                            {/* 系统通知 - SuperAdmin 专用 */}
                            {user.role === 'SuperAdmin' && (
                                <button
                                    onClick={() => setIsSystemNotificationModalOpen(true)}
                                    className="action-card action-card--indigo"
                                >
                                    <div className="action-card-icon">
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                        </svg>
                                    </div>
                                    <span className="action-card-title">{t('systemNotifications.openManager')}</span>
                                    <span className="action-card-subtitle">通知を管理</span>
                                </button>
                            )}

                            {/* 操作日志 */}
                            <button
                                onClick={fetchAuditLogs}
                                className="action-card action-card--gray"
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <span className="action-card-title">{t('viewAuditLogs')}</span>
                                <span className="action-card-subtitle">操作履歴</span>
                            </button>

                            {/* 重复照片检测 */}
                            {(user.role === 'Admin' || user.role === 'SuperAdmin') && (
                                <button
                                    onClick={() => setIsDuplicatePhotosModalOpen(true)}
                                    className="action-card action-card--orange"
                                >
                                    <div className="action-card-icon">
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <span className="action-card-title">重複照片検測</span>
                                    <span className="action-card-subtitle">写真の重複チェック</span>
                                </button>
                            )}
                        </div>

                        {/* 系统通知 - 移到快速操作卡片之后 */}
                        <SystemNotificationPanel />

                        {/* 统计分析区域 */}
                        <AdminAnalytics onInitialCostTalentCountClick={handleViewInitialCostTalents} />

                        {/* 支持信息区域 */}
                        <div className="welcome-section text-center">
                            <p className="text-base text-[var(--text-secondary)] mb-4">{t('selectManagementTask')}</p>
                            <div className="pt-4 border-t border-[var(--border)]">
                                <p className="text-sm text-[var(--text-muted)] mb-4">{t('feedbackPrompt')}</p>
                                <a
                                    href="https://www.larksuite.com/invitation/page/add_contact/?token=93fn2903-69a2-4e4b-a8be-2d8b49qvicbv"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="support-link"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    {t('contactSupport')}
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {view === 'talents' && (
                    <div className="collapsible-layout" style={{ minHeight: 'calc(100vh - 140px)' }}>
                        {/* 可折叠筛选面板 */}
                        <CollapsibleFilterPanel
                            isCollapsed={isFilterCollapsed}
                            onToggle={() => setIsFilterCollapsed(!isFilterCollapsed)}
                            config={filterConfig}
                            filters={filters}
                            onFiltersChange={setFilters}
                        />

                        {/* 主内容区域 */}
                        <div className="main-content-area">
                            <div className="mb-4 bg-[var(--surface)] p-4 rounded-lg shadow-[var(--shadow-sm)] border border-[var(--border)]">
                                <div className="flex justify-between items-center mb-2">
                                    <div className="text-sm text-[var(--text-secondary)]">
                                        {t('totalRecords')}: {totalCount}件 | {t('page')} {currentPage} / {totalPages}
                                    </div>
                                    <button
                                        onClick={handleExportCSV}
                                        disabled={isExporting || totalCount === 0}
                                        className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
                                    >
                                        {isExporting ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('downloading')}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                </svg>
                                                {t('exportCSV')}
                                            </>
                                        )}
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('searchPlaceholder')}
                                    className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary-200)] focus:border-[var(--primary-500)] transition-all duration-200"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* 表格横向滚动容器 */}
                            <div className="w-full">
                                <div className="bg-[var(--surface)] rounded-lg shadow-[var(--shadow-sm)] border border-[var(--border)]">
                                    <TalentTable
                                        talents={talents}
                                        isLoading={isTalentLoading}
                                        feeTotals={feeTotals}
                                        userInfo={user}
                                        onDataRefresh={fetchTalents}
                                        onEdit={handleEdit}
                                        onViewDetails={handleViewDetails}
                                        onViewEdit={handleViewEdit}
                                        onTransferTalent={handleTransferTalent}
                                        sortBy={sortBy}
                                        setSortBy={setSortBy}
                                        sortDirection={sortDirection}
                                        setSortDirection={setSortDirection}
                                    />
                                </div>
                            </div>

                            {/* 分页控件 */}
                            {totalPages > 1 && (
                                <div className="mt-4 flex justify-center items-center space-x-2 pb-4">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 bg-[var(--primary-600)] text-white rounded-lg disabled:bg-[var(--neutral-300)] disabled:cursor-not-allowed hover:bg-[var(--primary-700)] transition-colors"
                                    >
                                        {t('previousPage') || '前のページ'}
                                    </button>

                                    <div className="flex space-x-1">
                                        {[...Array(totalPages)].map((_, i) => {
                                            const page = i + 1;
                                            if (
                                                page === 1 ||
                                                page === totalPages ||
                                                (page >= currentPage - 2 && page <= currentPage + 2)
                                            ) {
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => setCurrentPage(page)}
                                                        className={`px-3 py-2 rounded-lg transition-colors ${page === currentPage
                                                            ? 'bg-[var(--primary-600)] text-white'
                                                            : 'bg-[var(--neutral-100)] hover:bg-[var(--neutral-200)] text-[var(--text-primary)]'
                                                            }`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            } else if (page === currentPage - 3 || page === currentPage + 3) {
                                                return <span key={page} className="px-2 text-[var(--text-muted)]">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 bg-[var(--primary-600)] text-white rounded-lg disabled:bg-[var(--primary-700)] transition-colors"
                                    >
                                        {t('nextPage') || '次のページ'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {view === 'logs' && <AuditLogTable logs={otherData} isLoading={isOtherLoading} />}

                {view === 'map' && <TalentMap />}

                {view === 'clients' && <ClientManagementPanel />}

                {view === 'jobseekers' && (user?.role === 'Admin' || user?.role === 'SuperAdmin') && <JobSeekerRegistrationsPanel />}

                {view === 'news' && <SpecifiedSkillNewsPanel />}
            </Layout>
        </>
    );
}
